// src/components/Contact.js
// Ethereal Command Center - Premium Contact Section with Firefly Particles

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import confetti from 'canvas-confetti';
import { Mail, MapPin, Send, CheckCircle, ArrowRight, Zap } from 'lucide-react';
import './Contact.css';

// Dust Particle System - Fine warm yellow particles like pin tips
const DustCanvas = ({ particleCount = 60 }) => {
  const canvasRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const animationRef = useRef(null);
  const particlesRef = useRef([]);

  // Warm yellow/gold tones only
  const colors = ['#FFF8DC', '#FFE4B5', '#FFEAA7', '#F5DEB3', '#FFD93D'];

  const createParticle = useCallback((width, height) => ({
    x: Math.random() * width,
    y: Math.random() * height,
    vx: (Math.random() - 0.5) * 0.2, // Slower drift
    vy: (Math.random() - 0.5) * 0.15 + 0.05, // Slight downward bias like dust
    size: Math.random() * 0.8 + 0.3, // Very tiny: 0.3-1.1px (pin tip size)
    baseOpacity: Math.random() * 0.4 + 0.3,
    opacity: 0,
    pulseSpeed: Math.random() * 0.008 + 0.003, // Slower, subtler pulse
    pulseOffset: Math.random() * Math.PI * 2,
    color: colors[Math.floor(Math.random() * colors.length)]
  }), []);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;

    const handleResize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width * window.devicePixelRatio;
      canvas.height = height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    // Initialize particles
    particlesRef.current = Array.from({ length: particleCount }, () =>
      createParticle(width, height)
    );

    let time = 0;

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      time += 0.016;

      particlesRef.current.forEach((p) => {
        // Update position - gentle floating
        p.x += p.vx;
        p.y += p.vy;

        // Wrap around edges
        if (p.x < -5) p.x = width + 5;
        if (p.x > width + 5) p.x = -5;
        if (p.y < -5) p.y = height + 5;
        if (p.y > height + 5) p.y = -5;

        // Subtle opacity pulse
        p.opacity = p.baseOpacity * (0.6 + 0.4 * Math.sin(time * p.pulseSpeed * 60 + p.pulseOffset));

        // Draw just a tiny sharp dot - no bloom/glow
        // Very subtle soft edge (tiny radius)
        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 1.5);
        gradient.addColorStop(0, p.color);
        gradient.addColorStop(0.5, p.color + Math.floor(p.opacity * 200).toString(16).padStart(2, '0'));
        gradient.addColorStop(1, 'transparent');

        ctx.globalAlpha = p.opacity;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Sharp core - the actual "pin tip"
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
        ctx.globalAlpha = 1;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [prefersReducedMotion, particleCount, createParticle]);

  if (prefersReducedMotion) return null;

  return <canvas ref={canvasRef} className="contact__fireflies" />;
};

// Info Card Component
const InfoCard = ({ icon: Icon, label, value, valueSub, href, iconClass, delay }) => (
  <motion.a
    href={href}
    target={href?.startsWith('http') ? '_blank' : undefined}
    rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
    className="contact__info-card"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className={`contact__info-card-icon ${iconClass}`}>
      <Icon size={22} />
    </div>
    <div className="contact__info-card-content">
      <span className="contact__info-card-label">{label}</span>
      <span className="contact__info-card-value">{value}</span>
      {valueSub && <span className="contact__info-card-value-sub">{valueSub}</span>}
    </div>
    <ArrowRight size={18} className="contact__info-card-arrow" />
  </motion.a>
);

// Celebration function - confetti burst with brand colors
const triggerCelebration = () => {
  const colors = ['#00D9FF', '#8B5CF6', '#FF6B9D', '#10B981'];

  // First burst
  confetti({
    particleCount: 80,
    spread: 60,
    origin: { y: 0.65 },
    colors,
    disableForReducedMotion: true
  });

  // Second burst (delayed)
  setTimeout(() => {
    confetti({
      particleCount: 50,
      spread: 100,
      origin: { y: 0.6 },
      colors,
      disableForReducedMotion: true
    });
  }, 150);
};

// Main Contact Component
const Contact = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const prefersReducedMotion = useReducedMotion();

  // Form state for Web3Forms
  const [formState, setFormState] = useState({
    submitting: false,
    succeeded: false,
    errors: null
  });

  // Handle form submission with Web3Forms
  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormState({ submitting: true, succeeded: false, errors: null });

    const formData = new FormData(event.target);
    formData.append("access_key", "a7d97c2b-12ed-4c27-a79c-132c5edd5bdb");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setFormState({ submitting: false, succeeded: true, errors: null });
        event.target.reset();
        // Trigger celebration animation
        if (!prefersReducedMotion) {
          triggerCelebration();
        }
      } else {
        setFormState({ submitting: false, succeeded: false, errors: data.message || 'Something went wrong' });
      }
    } catch (error) {
      setFormState({ submitting: false, succeeded: false, errors: 'Network error. Please try again.' });
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const formVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <section id="contact" className="contact">
      {/* Dust Particles Background */}
      <DustCanvas particleCount={60} />

      {/* Background Glows */}
      <div className="contact__bg-glow contact__bg-glow--cyan" />
      <div className="contact__bg-glow contact__bg-glow--violet" />

      <div className="contact__container" ref={ref}>
        {/* Header */}
        <motion.header
          className="contact__header"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={headerVariants}
        >
          <div className="contact__badge">
            <span>Get in Touch</span>
          </div>
          <h2 className="contact__title">
            Ready to Transform Your{' '}
            <span className="contact__title-gradient">Operations?</span>
          </h2>
          <p className="contact__subtitle">
            Let's discuss how Rosalytics can help you unlock operational intelligence.
          </p>
        </motion.header>

        {/* Bento Grid */}
        <div className="contact__bento">
          {/* Row 1: Info Cards */}
          <div className="contact__info-row">
            <InfoCard
              icon={Mail}
              label="Email"
              value="info@rosalytics.com"
              href="mailto:info@rosalytics.com"
              iconClass="contact__info-card-icon--email"
              delay={0.1}
            />
            <InfoCard
              icon={MapPin}
              label="Headquarters"
              value="Rosalytics"
              valueSub="Milwaukee, WI, United States"
              href="https://maps.google.com/?q=Milwaukee,WI,USA"
              iconClass="contact__info-card-icon--location"
              delay={0.15}
            />
          </div>

          {/* Row 2: Form */}
          <motion.div
            className="contact__form-card"
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={formVariants}
          >
            <div className="contact__form-inner">
              {formState.succeeded ? (
                <motion.div
                  className="contact__success"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <motion.div
                    className="contact__success-icon"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
                  >
                    <CheckCircle size={48} />
                  </motion.div>
                  <motion.h3
                    className="contact__success-title"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.4 }}
                  >
                    Message Sent!
                  </motion.h3>
                  <motion.p
                    className="contact__success-text"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6, duration: 0.4 }}
                  >
                    We'll get back to you within 4 hours during business hours.
                  </motion.p>
                </motion.div>
              ) : (
                <>
                  <h3 className="contact__form-title">Send Us a Message</h3>
                  <form onSubmit={handleSubmit} className="contact__form">
                    {/* Left Column - Fields */}
                    <div className="contact__form-left">
                      <div className="contact__field">
                        <label htmlFor="firstName" className="contact__label">
                          First Name *
                        </label>
                        <input
                          id="firstName"
                          type="text"
                          name="firstName"
                          required
                          placeholder="John"
                          className="contact__input"
                        />
                      </div>

                      <div className="contact__field">
                        <label htmlFor="lastName" className="contact__label">
                          Last Name *
                        </label>
                        <input
                          id="lastName"
                          type="text"
                          name="lastName"
                          required
                          placeholder="Doe"
                          className="contact__input"
                        />
                      </div>

                      <div className="contact__field">
                        <label htmlFor="email" className="contact__label">
                          Email *
                        </label>
                        <input
                          id="email"
                          type="email"
                          name="email"
                          required
                          placeholder="john@company.com"
                          className="contact__input"
                        />
                      </div>

                      <div className="contact__field">
                        <label htmlFor="company" className="contact__label">
                          Company
                        </label>
                        <input
                          id="company"
                          type="text"
                          name="company"
                          placeholder="Acme Corporation"
                          className="contact__input"
                        />
                      </div>
                    </div>

                    {/* Right Column - Message */}
                    <div className="contact__form-right">
                      <div className="contact__field" style={{ flex: 1 }}>
                        <label htmlFor="message" className="contact__label">
                          Message *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          placeholder="Tell us about your project or challenge..."
                          className="contact__textarea"
                        />
                      </div>

                      {/* Error Display */}
                      {formState.errors && (
                        <div className="contact__error">
                          {formState.errors}
                        </div>
                      )}

                      {/* Actions Row */}
                      <div className="contact__form-actions">
                        <motion.button
                          type="submit"
                          disabled={formState.submitting}
                          className="contact__submit-btn"
                          whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                          whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                        >
                          {formState.submitting ? (
                            <>
                              <div className="contact__spinner" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send size={18} />
                              Send Message
                            </>
                          )}
                        </motion.button>
                      </div>

                      {/* Response Time Assurance */}
                      <div className="contact__response-time">
                        <Zap size={14} className="contact__response-time-icon" />
                        <span className="contact__response-time-text">
                          We typically respond within <strong>4 hours</strong>
                        </span>
                      </div>
                    </div>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
